import { CommentIcon } from "components/Icons/CommentIcon";
import { Badge, CircularProgress, IconButton } from "@mui/material";
import React, { useMemo } from "react";
import { HIT_COMMENTS_ERROR_MSG, SearchVersion, HitStatus } from "aderant-conflicts-models";
import { HitsCardData } from "pages/ResultsPage/HitCardsPage";
import { HitStatusComboBoxWrapper } from "components/HitStatusComboBox/HitStatusComboBoxWrapper";
import { getSourceTypeDisplayValue } from "Functions/strings";
import { useSelector } from "react-redux";
import { RootState } from "MyTypes";

interface HitCardProps {
    hit: HitsCardData;
    isHitsCommentsLoaded: boolean;
    toggleRequestTermsDialog: () => void;
    searchVersion: SearchVersion;
    onHitClick?: (hit: HitsCardData) => void;
    onStatusChange?: (status?: HitStatus, hitOwnerId?: string | null) => void;
    searchReadOnly?: boolean;
    userChangePermissions?: any;
    hasReviewerRole?: boolean;
}

const HitCard = (props: HitCardProps) => {
    const { hit, isHitsCommentsLoaded, onHitClick, searchVersion, onStatusChange, searchReadOnly, userChangePermissions, hasReviewerRole } = props;

    const reviewerSharedStatuses = useSelector((state: RootState) => {
        const hitShared = state?.admin?.reviewerOptions?.hitShared;
        return Array.isArray(hitShared) ? hitShared : [];
    });

    const hitReadOnly = hasReviewerRole && hit?.hitStatus ? !reviewerSharedStatuses.includes(hit.hitStatus) : false;
    
    // Memoize styles to prevent unnecessary re-renders
    const iconButtonStyles = useMemo(() => ({
        padding: 0,
        "&:hover": {
            backgroundColor: "transparent"
        }
    }), []);

    const badgeStyles = useMemo(() => ({
        "& .MuiBadge-badge": {
            fontSize: "0.75rem",
            height: "12px",
            minWidth: "12px",
            paddingBottom: "5px",
            backgroundColor: "transparent",
            left: "50%",
            color: "white"
        }
    }), []);

    const handleCardClick = () => {
        if (onHitClick) {
            onHitClick(hit);
        }
    };

    return (
        <div className="cards" onClick={handleCardClick} style={{ cursor: "pointer" }}>
            <div className="cards-header">
                <div style={{ width: "200px", height: "35px" }}>
                    <div className="header-hitName">{hit?.hitName}</div>
                    <div className="header-hitId">{`ID ${hit?.hitId}`}</div>
                </div>
                <div 
                    className="hit-status-container" 
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: "200px" }}
                >
                    <HitStatusComboBoxWrapper
                        id={`hit-status-dropdown-${hit?.hitId}`}
                        disabled={searchReadOnly || !userChangePermissions.hitStatus || hitReadOnly}
                        hitStatus={hit?.hitStatus}
                        searchVersion={searchVersion}
                        onStatusChange={onStatusChange}
                        style={{ width: "100%" }}
                    />
                </div>
            </div>

            <div className="cards-hitContent">
                {isHitsCommentsLoaded ? (
                    hit?.hasCommentError ? (
                        <div className="comment-error">
                            <span style={{ color: "red", fontWeight: "bold", fontSize: "16px" }}>! </span>
                            {HIT_COMMENTS_ERROR_MSG}
                        </div>
                    ) : Number(hit?.hitCommentCount) > 0 ? (
                        <div className="hit-comment">
                            <div>
                                <div className="comment-name">{hit?.commentedBy}</div>
                                <span>{hit?.latestCommentedOn}</span>

                                <div className="comment-body">{hit?.latestCommentText}</div>
                            </div>
                            <div style={{ marginLeft: "auto" }}>
                                <IconButton
                                    disableRipple
                                    sx={iconButtonStyles}
                                >
                                    <Badge
                                        badgeContent={hit?.hitCommentCount}
                                        className="chat-badge"
                                        sx={badgeStyles}
                                    />
                                    <CommentIcon width={24} height={24} />
                                </IconButton>
                            </div>
                        </div>
                    ) : null
                ) : (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                        <CircularProgress size="30px" color="inherit" />
                    </div>
                )}

                <div>
                    <div className="label">Hit Type</div>
                    <div className="label-value">{getSourceTypeDisplayValue(hit?.hitType)}</div>
                </div>

                <div>
                    <div className="label">Client</div>
                    <div className="label-value">{hit?.client}</div>
                </div>

                <div>
                    <div className="label">Matter</div>
                    <div className="label-value">{hit?.matter}</div>
                </div>
            </div>
        </div>
    );
};

export default HitCard;
